import {
  lerArquivo, colunasFaltando, validarRegistro, relatorioErrosCsv, baixarModelo, labelDoCampo,
} from "./loteArquivo";

/**
 * Inclusão em Lote — fluxo:
 *  1. Verificar: lê a planilha (.xlsx/.xls/.csv), valida cada linha no navegador e manda as linhas
 *     válidas para a pré-validação do backend (validarLoteNegativar: prazos, espécie, duplicidade).
 *  2. Enviar: só com zero erros; manda tudo para sendLoteNegativar (tudo ou nada).
 * Documentação: stoneup-client/doc/INCLUSAO_LOTE.md
 */
export default {
  data: () => ({
    alertar: false,
    especies: [],
    anexo_lote: null,

    // inicial | verificando | com_erros | pronto | enviando | enviado
    etapa: "inicial",
    colunasAusentes: [],
    erroLeitura: "",

    linhas_total: 0,
    linhas_com_erros: 0,
    linhas_com_sucesso: 0,

    erros: [],
    arquivo_lote: [],
    resultado: null,

    errosHeaders: [
      { text: "Linha", value: "linha", width: 80 },
      { text: "Coluna", value: "coluna", width: 200 },
      { text: "Valor informado", value: "valor", width: 200 },
      { text: "O que corrigir", value: "mensagem", sortable: false },
    ],
  }),

  computed: {
    errosTabela() {
      return this.erros.map((e, i) => ({ ...e, id: i, coluna: labelDoCampo(e.campo) }));
    },
    podeEnviar() {
      return this.etapa === "pronto" && this.arquivo_lote.length > 0;
    },
  },

  watch: {
    // Trocar de arquivo invalida a verificação anterior (antes dava para enviar o arquivo antigo)
    anexo_lote() {
      this.limparResultado();
    },
  },

  mounted() {
    this.listarEspecies();
    const empresas = this.$store.state.auth.user.empresas;
    if (Array.isArray(empresas) && empresas.length <= 0) {
      this.alertar = true;
    }
  },

  methods: {
    listarEspecies() {
      this.$http.get("getEspecies").then(
        (response) => { this.especies = response.data; },
        () => { this.especies = []; }
      );
    },

    cadastrarEmpresa() {
      this.$router.push("/empresas");
    },

    baixarModelo() {
      baixarModelo(this.especies);
    },

    limparResultado() {
      this.etapa = "inicial";
      this.colunasAusentes = [];
      this.erroLeitura = "";
      this.linhas_total = 0;
      this.linhas_com_erros = 0;
      this.linhas_com_sucesso = 0;
      this.erros = [];
      this.arquivo_lote = [];
      this.resultado = null;
    },

    recarregar() {
      this.anexo_lote = null;
      this.limparResultado();
    },

    async verificarArquivo() {
      if (!this.anexo_lote) return;
      this.limparResultado();
      this.etapa = "verificando";

      let planilha;
      try {
        planilha = await lerArquivo(this.anexo_lote);
      } catch (e) {
        this.etapa = "inicial";
        this.erroLeitura = "Não foi possível ler o arquivo. Salve-o como Excel (.xlsx) ou CSV e tente de novo.";
        return;
      }

      this.colunasAusentes = colunasFaltando(planilha.cabecalho);
      if (this.colunasAusentes.length) {
        this.etapa = "inicial";
        return;
      }
      if (!planilha.registros.length) {
        this.etapa = "inicial";
        this.erroLeitura = "A planilha não tem nenhuma linha preenchida abaixo do cabeçalho.";
        return;
      }

      if (!this.especies.length) {
        await new Promise((resolve) => this.$http.get("getEspecies").then(
          (r) => { this.especies = r.data; resolve(); }, () => resolve()
        ));
      }

      const erros = [];
      const validas = [];
      planilha.registros.forEach(({ linha, valores }) => {
        const r = validarRegistro(valores, this.especies);
        if (r.erros.length) erros.push(...r.erros.map((e) => ({ linha, ...e })));
        else validas.push({ linha, ...r.dados });
      });

      // Linhas que passaram aqui ainda passam pelas regras do servidor (prazo máximo, espécie, títulos repetidos)
      if (validas.length) {
        try {
          const resposta = await this.$http.post("validarLoteNegativar", validas);
          erros.push(...((resposta.body || resposta.data || {}).erros || []));
        } catch (resposta) {
          this.etapa = "inicial";
          this.notificarFalha(resposta, "Não foi possível validar o arquivo no servidor");
          return;
        }
      }

      this.aplicarErros(planilha.registros.length, erros);
      this.arquivo_lote = this.erros.length ? [] : validas;
    },

    aplicarErros(total, erros) {
      this.erros = erros.slice().sort((a, b) => (a.linha || 0) - (b.linha || 0));
      const linhasComErro = new Set(this.erros.map((e) => e.linha));
      this.linhas_total = total;
      this.linhas_com_erros = linhasComErro.size;
      this.linhas_com_sucesso = total - linhasComErro.size;
      this.etapa = this.erros.length ? "com_erros" : "pronto";
    },

    async enviarLote() {
      if (!this.podeEnviar) return;
      this.etapa = "enviando";
      try {
        const resposta = await this.$http.post("sendLoteNegativar", this.arquivo_lote);
        const corpo = resposta.body || resposta.data || {};
        this.resultado = {
          lote: corpo.lote,
          quantidade: (corpo.documentos_inserios || []).length || this.arquivo_lote.length,
        };
        this.etapa = "enviado";
      } catch (resposta) {
        const corpo = (resposta && (resposta.body || resposta.data)) || {};
        if (Array.isArray(corpo.erros) && corpo.erros.length) {
          // Algo mudou entre a verificação e o envio (ex.: título negativado nesse meio-tempo)
          this.aplicarErros(this.linhas_total, corpo.erros);
          this.arquivo_lote = [];
          this.$swal({ icon: "error", title: "Envio recusado", text: "Nada foi gravado. Corrija os pontos listados e verifique o arquivo de novo." });
        } else {
          this.etapa = "pronto";
          this.notificarFalha(resposta, "Não foi possível enviar o lote");
        }
      }
    },

    baixarErros() {
      const blob = new Blob([relatorioErrosCsv(this.erros)], { type: "text/csv;charset=utf-8" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      const nome = (this.anexo_lote && this.anexo_lote.name ? this.anexo_lote.name.replace(/\.[^.]+$/, "") : "lote");
      link.download = `Erros - ${nome}.csv`;
      link.click();
      URL.revokeObjectURL(link.href);
    },

    verTitulos() {
      this.$router.push({ name: "Consulta de Títulos" });
    },

    notificarFalha(resposta, titulo) {
      const corpo = (resposta && (resposta.body || resposta.data)) || {};
      this.$swal({
        icon: "error",
        title: titulo,
        text: typeof corpo.message === "string" && corpo.message
          ? corpo.message
          : "Erro inesperado. Tente novamente em instantes; se persistir, fale com o suporte.",
      });
    },
  },
};
